import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaPlay, FaPause } from 'react-icons/fa';
import useScrollAnimation from './useScrollAnimation';

const highlightsData = [
  {
    id: 1,
    tag: 'Design & Materials',
    title: 'Forged in titanium.',
    description: 'The premium aerospace-grade titanium design makes Peace i17 Air our lightest, most durable flagship ever created.',
    image: '/Photo/i7 air 2.jpg',
    badge: 'Grade 5 Alloy'
  },
  {
    id: 2,
    tag: 'Next-Gen Silicon',
    title: 'A17 Pro monster chip.',
    description: 'Groundbreaking GPU architecture delivers high-fidelity ray tracing and desktop-class graphics performance.',
    image: '/Photo/i17air-1.jpg',
    badge: '3nm Architecture'
  },
  {
    id: 3,
    tag: 'Pro Camera System',
    title: '48MP Photonic powerhouse.',
    description: 'Capture next-generation portraits with focus and depth control, plus up to 5x optical zoom at 120mm.',
    image: '/Photo/17pro max .jpg',
    badge: '5x Optical Zoom'
  },
  {
    id: 4,
    tag: 'Control & Action',
    title: 'All-new Action button.',
    description: 'A fast-track to your favorite feature. Set it to Silent mode, Camera, Voice Memo, Flashlight, or custom Shortcuts.',
    image: '/Photo/SC26 front.jpg',
    badge: 'Instant Trigger'
  }
];

const HighlightsSection = () => {
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.15 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  // Auto slide ticker
  React.useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % highlightsData.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [isPlaying]);

  return (
    <section 
      ref={sectionRef} 
      className={`highlights-section ${isVisible ? 'is-visible' : ''}`}
      id="highlights"
    >
      <div className="section-container">
        {/* Section Header */}
        <div className="section-header-row scroll-fade-up">
          <div>
            <span className="section-eyebrow">Innovation Highlights</span>
            <h2 className="section-headline">Get the highlights.</h2>
          </div>
          <div className="section-header-action">
            <Link to="/products" className="apple-text-link">
              Explore all models <FaChevronRight className="link-arrow" />
            </Link>
          </div>
        </div>

        {/* Interactive Main Highlight Feature Card */}
        <div className="highlight-main-stage scroll-scale-up">
          <div className="highlight-card-backdrop">
            <img 
              src={highlightsData[activeIndex].image} 
              alt={highlightsData[activeIndex].title}
              className="highlight-feature-img"
            />
            <div className="highlight-gradient-overlay" />
          </div>

          <div className="highlight-content-box">
            <span className="highlight-badge-pill">
              {highlightsData[activeIndex].badge}
            </span>
            <span className="highlight-category">
              {highlightsData[activeIndex].tag}
            </span>
            <h3 className="highlight-card-title">
              {highlightsData[activeIndex].title}
            </h3>
            <p className="highlight-card-desc">
              {highlightsData[activeIndex].description}
            </p>
          </div>

          {/* Media Play / Pause Toggle */}
          <button 
            className="highlight-play-toggle"
            onClick={() => setIsPlaying(!isPlaying)}
            aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
        </div>

        {/* Interactive Indicator Pills */}
        <div className="highlight-nav-pills scroll-fade-up">
          {highlightsData.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveIndex(idx);
                setIsPlaying(false);
              }}
              className={`highlight-nav-item ${activeIndex === idx ? 'active' : ''}`}
            >
              <div className="pill-progress-bar">
                <div 
                  className={`pill-fill ${activeIndex === idx && isPlaying ? 'animating' : ''}`} 
                />
              </div>
              <span className="pill-title">{item.title}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HighlightsSection;
